import React, { useState, useRef, useEffect } from 'react';
import { Building, Check, ChevronDown, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CreateOrgModal } from './CreateOrgModal';

export const OrganizationSelector: React.FC = () => {
  const { organizations, currentOrg, switchOrganization } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentOrg) return null;

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900/90 border border-slate-800 hover:border-slate-700 text-xs font-medium text-slate-200 transition-colors"
        >
          <Building className="w-3.5 h-3.5 text-brand-400" />
          <span className="font-semibold max-w-[120px] truncate">
            {currentOrg.name}
          </span>
          <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-slate-800 text-slate-400">
            {currentOrg.role}
          </span>
          <ChevronDown className="w-3 h-3 text-slate-500" />
        </button>

        {isOpen && (
          <div className="absolute left-0 mt-2 w-64 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-2 z-50">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-1.5">
              Workspaces ({organizations.length})
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1">
              {organizations.map((org) => {
                const isSelected = org.id === currentOrg.id;
                return (
                  <button
                    key={org.id}
                    onClick={() => {
                      switchOrganization(org.id);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs transition-colors ${
                      isSelected
                        ? 'bg-brand-600/15 text-brand-300 border border-brand-500/20'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className="flex flex-col text-left truncate mr-2">
                      <span className="font-semibold truncate">{org.name}</span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        role: {org.role.toLowerCase()}
                      </span>
                    </div>
                    {isSelected && <Check className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 pt-2 border-t border-slate-800">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setModalOpen(true);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-brand-400 hover:bg-brand-500/10 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create Workspace</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateOrgModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
